import React from "react";
import { observer } from "mobx-react";
import { CustomPricingInputModel } from "../models";
import {
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  nameof,
} from "@mindmemobile/mm-shared-global";
import { DescriptionCell, LabelCell } from "../styles";
import { AccountSettingsStore } from "../store";

type IProps = {
  customPricing: CustomPricingInputModel;
  onUpdate: (data: CustomPricingInputModel) => void;
  renderAsLabels?: boolean;
};

class CustomPricing extends React.Component<IProps> {
  formatAmount = (value: string): string => {
    if (parseFloat(value) < 0 || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(value.toString().replace(/,/g, "") || "0.00"),
      true
    );
  };

  onChange(
    value: string,
    key: keyof CustomPricingInputModel,
    isBlur?: boolean
  ) {
    const clonedObject = new CustomPricingInputModel({
      ...this.props.customPricing,
    });

    switch (key) {
      case nameof<CustomPricingInputModel>("minimum"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.minimum = value;
        break;
      case nameof<CustomPricingInputModel>("maximum"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.maximum = value;
        break;
      case nameof<CustomPricingInputModel>("suggestion"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.suggestion = value;
        break;
      case nameof<CustomPricingInputModel>("cost"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.cost = value;
        break;
      case nameof<CustomPricingInputModel>("setUpFee"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.setUpFee = value;
        break;
    }

    this.props.onUpdate(new CustomPricingInputModel(clonedObject));
  }

  hasError(fieldName: keyof CustomPricingInputModel): boolean {
    const error = this.props.customPricing.errors?.find(
      (f) => f.field === fieldName
    );
    return error !== undefined;
  }

  input(
    value: string,
    fieldName: keyof CustomPricingInputModel,
    showPlaceHolder?: boolean
  ) {
    if (this.props.renderAsLabels) {
      return (
        <div>
          {value
            ? `$${AccountSettingsStore.formatAmount(parseFloat(value))}`
            : "-"}
        </div>
      );
    }
    return (
      <div>
        <FlexContainer spacing={15} fxLayout="row" className="dollar-usd-input">
          <div>
            <Input
              value={value}
              error={this.hasError(nameof<CustomPricingInputModel>(fieldName))}
              id={`txt_${fieldName}`}
              type="text"
              placeholder={showPlaceHolder ? "0.00" : ""}
              onChange={(e, isBlur) =>
                this.onChange(e.target.value, fieldName, isBlur)
              }
            />
          </div>
        </FlexContainer>
      </div>
    );
  }

  render(): React.ReactNode {
    const { customPricing } = this.props;
    return (
      <>
        {customPricing.showSetUpFee && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel lineHeight="40px" fontSize="16px">
                {" "}
                Setup Fee
              </CustomLabel>
              <InfoIcon />
            </LabelCell>
            <DescriptionCell>
              {this.input(
                customPricing.setUpFee,
                nameof<CustomPricingInputModel>("setUpFee"),
                true
              )}
            </DescriptionCell>
          </FlexContainer>
        )}
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel>Price Suggestion</CustomLabel>
            <InfoIcon />
          </LabelCell>
          <DescriptionCell>
            {this.input(
              customPricing.suggestion,
              nameof<CustomPricingInputModel>("suggestion"),
              true
            )}
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel>Price Restrictions</CustomLabel>
            <InfoIcon />
          </LabelCell>
          <DescriptionCell>
            <FlexContainer alignItems="center" spacing={20}>
              <div className="prc-restr-input">
                {this.input(
                  customPricing.minimum,
                  nameof<CustomPricingInputModel>("minimum"),
                  true
                )}
                <CustomLabel fontSize={"15px"}>Minimum</CustomLabel>
              </div>
              <div>
                {this.input(
                  customPricing.maximum,
                  nameof<CustomPricingInputModel>("maximum"),
                  true
                )}{" "}
                <CustomLabel fontSize={"15px"}>Maximum</CustomLabel>
              </div>
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel>Cost</CustomLabel>
            <InfoIcon />
          </LabelCell>
          <DescriptionCell>
            {this.input(
              customPricing.cost,
              nameof<CustomPricingInputModel>("cost")
            )}
          </DescriptionCell>
        </FlexContainer>
      </>
    );
  }
}

export default observer(CustomPricing);
